import React, { useState } from "react";
import {
  Drawer,
  Box,
  Typography,
  useTheme,
  Divider,
  Dialog,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { getStyles } from "../index";

import { VideoPlayer } from "./HelpDialogItems";

const InfoDrawer = ({
  drawerType,
  open = false,
  onClose,
  permanentlyOpen = false,
  width = "33%",
}) => {
  const theme = useTheme();
  const styles = getStyles(theme);

  // Add state for video dialog
  const [videoDialogOpen, setVideoDialogOpen] = useState(false);

  // Video details
  const videoUrl = "https://sportkuriren.se/wp-content/uploads/bibeln-2.mp4";
  const posterUrl = "https://sportkuriren.se/wp-content/uploads/bibeln-2.png";

  const videoUrlMath =
    "https://sportkuriren.se/wp-content/uploads/assistent-matte.mp4";
  const posterUrlMath =
    "https://sportkuriren.se/wp-content/uploads/assistent-matte.png";

  const videoUrlSpanish =
    "https://sportkuriren.se/wp-content/uploads/assistent-sprak-spanska.mp4";
  const posterUrlSpanish =
    "https://sportkuriren.se/wp-content/uploads/assistent-sprak-spanska.png";

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      variant={permanentlyOpen ? "permanent" : "temporary"}
      PaperProps={{
        sx: {
          display: { xs: "none", md: "block" },
          width: { xs: "75%", sm: "50%", md: width },
          boxShadow: "none",
          borderLeft:
            theme.palette.mode === "dark"
              ? "1px solid #34414c"
              : "1px solid #e0e0e0",
          backgroundImage: "none",
        },
      }}
      ModalProps={{
        keepMounted: permanentlyOpen,
        disableEnforceFocus: permanentlyOpen,
        disableAutoFocus: permanentlyOpen,
        hideBackdrop: permanentlyOpen,
      }}
    >
      <Box sx={{ p: 3 }}>
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
          {drawerType === "roleplay"
            ? "Hur rollspel fungerar"
            : "Hur detta fungerar"}
        </Typography>
        <Divider sx={{ mb: 3 }} />
        <Typography
          variant="body1"
          sx={{
            mb: 3,
            color: "text.secondary",
            lineHeight: "1.7rem",
          }}
        >
          {drawerType === "roleplay" ? (
            `Rollspel låter dig delta i interaktiva scenarier med AI-karaktärer.
    Välj ett rollspel och börja en konversation för att öva
    språkfärdigheter på ett roligt och engagerande sätt.`
          ) : drawerType === "assistants" ? (
            <>
              Här finns det assistenter man kan kan chatta och tala med för
              olika skolämnen som t.ex. matematik, historia och fysik.
              <br />
              <br />
              Man kan <strong>exportera, kopiera</strong> eller{" "}
              <strong>spara svar</strong>. Tillgång till historik över chattar
              samt möjlighet att <strong>dela konversationer</strong> med andra.
            </>
          ) : drawerType === "languages" ? (
            <>
              Här finns det assistenter man kan kan chatta och tala med för att
              lära sig olika språk så som{" "}
              <strong>franska, tyska, engelska & spanska</strong>. Det finns
              även en mycket populär <strong>SFI-lärare</strong>.
              <br />
              <br />
              Man kan enkelt starta olika rollspel, öva på läsförståelse, verb
              och mycket mer.
              <br />
              <br />
              Lär dig språk i en miljö{" "}
              <strong>fri från stress och press</strong>.
            </>
          ) : (
            <>
              Dessa assistenter har tillgång till specialiserade kunskapsbaser.
              När du ställer en fråga genomförs en avancerad{" "}
              <strong>semantisk sökning</strong> som identifierar de mest
              relevanta textavsnitten från originalkällan.
              <br />
              <br /> Assistenten analyserar sedan dessa avsnitt och formulerar
              ett{" "}
              <strong>
                {" "}
                svar som är direkt baserat på bokens faktiska innehåll
              </strong>
              . Du kan ställa frågor om precis vad du vill inom bokens
              ämnesområde och få ett välgrundat svar som är{" "}
              <strong>förankrat i originalverket</strong>.
            </>
          )}
        </Typography>
        <Box sx={{ mt: 8 }}>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
            {drawerType === "assistants"
              ? "Exempel från Matematik-assistenten:"
              : drawerType === "languages"
                ? "Exempel från Spanska-läraren"
                : "Exempel från Bibeln:"}
          </Typography>
        </Box>

        <Box
          onClick={() => setVideoDialogOpen(true)}
          sx={{
            cursor: "pointer",
            position: "relative",
            borderRadius: 4,
            "&:hover": {
              opacity: 0.9,
            },
          }}
        >
          <img
            src={
              drawerType === "assistants"
                ? posterUrlMath
                : drawerType === "languages"
                  ? posterUrlSpanish
                  : posterUrl
            }
            alt="Video thumbnail"
            style={{ width: "100%", display: "block", borderRadius: 10 }}
          />
          <Box
            className="play-button"
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              width: "80px",
              height: "80px",
              borderRadius: "50%",
              backgroundColor: "rgb(0 0 0 / 83%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "transform 0.2s",
              pointerEvents: "none",
              "&:hover": {
                transform: "translate(-50%, -50%) scale(1.1)",
              },
            }}
          >
            <PlayArrowIcon sx={{ color: "white", fontSize: "4rem" }} />
          </Box>
        </Box>

        {/* Video Dialog */}
        <Dialog
          open={videoDialogOpen}
          onClose={() => setVideoDialogOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: { backgroundColor: "transparent" },
          }}
        >
          <Box sx={{ position: "relative" }}>
            <IconButton
              aria-label="close"
              onClick={() => setVideoDialogOpen(false)}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: "white",
                backgroundColor: "rgba(0,0,0,0.5)",
                zIndex: 1,
                "&:hover": {
                  backgroundColor: "rgba(0,0,0,0.7)",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
            <VideoPlayer
              videoUrl={
                drawerType === "assistants"
                  ? videoUrlMath
                  : drawerType === "languages"
                    ? videoUrlSpanish
                    : videoUrl
              }
              poster={
                drawerType === "assistants"
                  ? posterUrlMath
                  : drawerType === "languages"
                    ? posterUrlSpanish
                    : posterUrl
              }
              autoPlay
              controls
            />
          </Box>
        </Dialog>

        {drawerType === "dialogue" && (
          <>
            <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
              Dialoginstruktioner
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              Denna funktion låter dig öva konversationer i olika verkliga
              situationer. Varje dialog har specifika mål för att hjälpa dig
              förbättra dina språkfärdigheter.
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              Följ instruktionerna och försök att svara naturligt som du skulle
              i en verklig konversation.
            </Typography>
            <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 500 }}>
              Handledningsvideo:
            </Typography>
            <Box
              sx={{
                width: "100%",
                height: 200,
                backgroundColor: "#f0f0f0",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                borderRadius: 1,
                border: "1px dashed #ccc",
                mb: 2,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Videoplacering
              </Typography>
            </Box>
          </>
        )}
      </Box>
    </Drawer>
  );
};

export default InfoDrawer;
